import type { Event, EventHint } from '@sentry/react'

/** Stack frames and script URLs from browser extensions — not app bugs. */
const EXTENSION_URL_PATTERN =
  /^(chrome|chrome-extension|moz-extension|safari-extension|safari-web-extension|ms-browser-extension):/i

/** Error messages / filenames commonly emitted by extensions (LastPass, password managers, ad blockers). */
const EXTENSION_NOISE_PATTERNS: RegExp[] = [
  /lastpass/i,
  /background-redux-new\.js/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
]

const EXTENSION_OPERATION_ERROR =
  /^OperationError\b/i

function collectFrameStrings(event: Event): string[] {
  const parts: string[] = []
  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (value.value) parts.push(value.value)
      if (value.type) parts.push(value.type)
      for (const frame of value.stacktrace?.frames ?? []) {
        if (frame.filename) parts.push(frame.filename)
        if (frame.abs_path) parts.push(frame.abs_path)
      }
    }
  }
  if (typeof event.message === 'string') {
    parts.push(event.message)
  }
  for (const log of event.breadcrumbs ?? []) {
    if (typeof log.message === 'string') parts.push(log.message)
  }
  return parts
}

/** True when stack / URL points at a browser extension, not our bundle. */
export function eventFromBrowserExtension(event: Event): boolean {
  const haystack = collectFrameStrings(event)
  return haystack.some((part) => EXTENSION_URL_PATTERN.test(part.trim()))
}

/** True for known third-party extension noise (e.g. LastPass OperationError). */
export function isKnownThirdPartyNoise(event: Event): boolean {
  const haystack = collectFrameStrings(event)
  const combined = haystack.join('\n')

  if (EXTENSION_NOISE_PATTERNS.some((pattern) => pattern.test(combined))) {
    return true
  }

  const primaryType = event.exception?.values?.[0]?.type ?? ''
  const primaryValue = event.exception?.values?.[0]?.value ?? ''
  if (
    EXTENSION_OPERATION_ERROR.test(primaryType) ||
    EXTENSION_OPERATION_ERROR.test(primaryValue)
  ) {
    // OperationError alone is ambiguous; drop only when extension URLs appear too.
    return EXTENSION_URL_PATTERN.test(combined) || /lastpass/i.test(combined)
  }

  return false
}

export function sanitizeSentryRequest(event: Event): Event {
  if (event.request?.url) {
    event.request.url = event.request.url.replace(
      /[?&](api[_-]?key|token|password)=[^&]*/gi,
      '',
    )
  }
  return event
}

/** Sentry performance insight when many parallel fetches hit the same API route. */
export function isConsecutiveHttpPerformanceNoise(event: Event): boolean {
  const title = typeof (event as { title?: string }).title === 'string' ? (event as { title?: string }).title! : ''
  const message = typeof event.message === 'string' ? event.message : ''
  const transaction = event.transaction ?? ''
  const blob = `${title} ${message} ${transaction}`.toLowerCase()
  return blob.includes('consecutive http')
}

/** Drop extension / third-party noise; sanitize sensitive query params. */
export function filterSentryEvent(event: Event, _hint?: EventHint): Event | null {
  if (
    eventFromBrowserExtension(event) ||
    isKnownThirdPartyNoise(event) ||
    isConsecutiveHttpPerformanceNoise(event) ||
    isTransientNetworkFetchNoise(event)
  ) {
    return null
  }
  return sanitizeSentryRequest(event)
}

export const sentryDenyUrls: Array<string | RegExp> = [
  /extensions\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
  /^ms-browser-extension:\/\//i,
]

export const sentryIgnoreErrors: Array<string | RegExp> = [
  /^OperationError/i,
  /lastpass/i,
  /background-redux-new\.js/i,
  /^TypeError: Failed to fetch/i,
  /NetworkError/i,
]

/** Client read paths degrade on network blips — not product defects. */
export function isTransientNetworkFetchNoise(event: Event): boolean {
  const primary = event.exception?.values?.[0]
  if (!primary) return false
  const combined = `${primary.type ?? ''}: ${primary.value ?? ''}`
  return (
    /Failed to fetch/i.test(combined) ||
    /NetworkError/i.test(combined) ||
    /Load failed/i.test(combined)
  )
}

/** @deprecated Use isTransientNetworkFetchNoise */
export const isTransientBillingFetchNoise = isTransientNetworkFetchNoise
